import React, { useEffect } from 'react';
import { Button, Row, Col, Alert, Tooltip } from 'antd';
import { EyeOutlined, StopOutlined } from '@ant-design/icons';
import { CustomModal, CustomTable, GeneralContainer } from '../../../../components';
import { ShareResponse } from '../../../../share';
import { TechnicalRequestResponse } from '../../types/technical-concept';
import SearchRow from '../components/SearchFilters';
import useApproveRequestsFormik from '../../../../utils/validators/approve-request/approve-request-validator';
import CancelRequest from '../../../ApproveRequests/components/components/ByRoles/CancelRequest';
import { TechnicalConceptAPI } from '../../api/technicalConceptAPI';
import CustomAlert from '../../../../components/CustomAlert/CustomAlert';

interface TechnicalConceptViewProps {
  data?: TechnicalRequestResponse;
  onViewCreate: () => void;
  onPagination: (page: number, pageSize: number) => void;
  onViewDetail: (id: string) => void;
  formik: any;
  isLoading: boolean;
  onResetFilters: () => void;
  supervisors?: ShareResponse[];
  setIsModalCustomFilter?: any;
  movements?: any[];
  onCancelRequest: (id: string, reason: string) => Promise<void>;
}

const TechnicalConceptView: React.FC<TechnicalConceptViewProps> = ({
  data,
  onViewCreate,
  onViewDetail,
  onPagination,
  formik,
  isLoading,
  onResetFilters,
  onCancelRequest,
}) => {
  const [modalCancel, setModalCancel] = React.useState(false);
  const [requestId, setRequestId] = React.useState('');
  const [status, setStatus] = React.useState<{ id: string; name: string }[]>([]);

  const onConfirmModal = () => {
    onCancelRequest(requestId, formikCancelRequest.values.reason).finally(() => {
      setModalCancel(false);
      formikCancelRequest.resetForm();
    });
  }


  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const data = await TechnicalConceptAPI.fetchParameters(['concept_petition_status']);
        setStatus(data?.concept_petition_status?.map((item: any) => ({
          id: String(item.id),
          name: item.status,
        })) || []);
      } catch (error) {
        throw error;
      }
    };
    fetchStatus();
  }, [data]);

  const columns = [
    {
      title: 'Número ticket',
      dataIndex: 'ticket',
      key: 'ticket',
      sorter: true,
      align: 'center',
    },
    {
      title: 'Tipo ticket',
      dataIndex: 'ticket_type',
      key: 'ticket_type',
      sorter: true,
      align: 'center',
      responsive: ['lg'],
    },
    {
      title: 'Tipo de bien',
      dataIndex: 'goods_type',
      key: 'goods_type',
      sorter: true,
      align: 'center',
      responsive: ['lg'],
    },
    {
      title: 'Fecha de solicitud',
      dataIndex: 'query_date',
      key: 'query_date',
      sorter: true,
      align: 'center',
    },
    {
      title: 'Regional',
      dataIndex: 'regional',
      key: 'regional',
      sorter: true,
      align: 'center',
      responsive: ['lg'],
    },
    {
      title: 'Seccional',
      dataIndex: 'sectional',
      key: 'sectional',
      sorter: true,
      align: 'center',
      responsive: ['lg'],
    },
    {
      title: 'Estado de solicitud',
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      render: (status: any) => {
        return <CustomAlert status={status} />
      },
    },
    {
      title: 'Solicitante',
      dataIndex: 'requester',
      key: 'requester',
      align: 'center',
      responsive: ['lg'],
    },
    {
      title: 'Observaciones',
      dataIndex: 'observations',
      key: 'observations',
      align: 'center',
      width: 300,
      render: (text: string) => {
        return (
          <Tooltip title={text || 'Sin registro'}>
            <div
              style={{
                maxWidth: 250,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                margin: '0 auto',
              }}
            >
              {text || 'Sin registro'}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Acciones',
      key: 'actions',
      align: 'center',
      render: (_: any, record: { id: number; petition_id: number; status_id: string }) => (
        <>
          <Tooltip title="Ver solicitud">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => onViewDetail(record.id.toString())}
            />
          </Tooltip>
          {['67'].includes(record.status_id) && <Tooltip title="Anular solicitud">
            <Button
              type="link"
              icon={<StopOutlined />}
              onClick={() => {
                setRequestId(record.petition_id.toString())
                setModalCancel(true);
              }}
            />
          </Tooltip>}
        </>
      ),
    },
  ];

  const formikCancelRequest = useApproveRequestsFormik({ onSubmit: () => { } });
  return (
    <GeneralContainer
      title="Solicitudes de conceptos técnicos"
      actionLabel="Crear solicitud"
      onActionClick={onViewCreate}
      isLoading={isLoading}
    >
      {/* Filtros */}
      <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
        <Col xs={24}>
          <SearchRow
            formik={formik}
            states={status}
            onCancel={onResetFilters}
            isLoading={isLoading}
          />
        </Col>
      </Row>

      <Row>
        <Col xs={24}>
          <CustomTable
            loading={isLoading}
            columns={columns as any}
            dataSource={data?.data}
            paginationConfig={{
              current: data?.current_page,
              pageSize: data?.per_page,
              total: data?.total,
              onChange: onPagination,
            }}
            scroll={{ x: 'max-content' }}
          />
        </Col>
      </Row>

      <CustomModal
        title=""
        labelSubmit="Aceptar"
        open={modalCancel}
        width={1000}
        onCancel={() => setModalCancel(false)}
        onSave={onConfirmModal}
        disabledBtn={!formikCancelRequest.values.reason}
      >
        <CancelRequest
          formik={formikCancelRequest}
        />
      </CustomModal>
    </GeneralContainer>
  );
};

export default TechnicalConceptView;
